import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthProcessService } from '../auth-process.service';
import { isNil, isError } from 'lodash';

interface IVerifyEmailContext {
  email: string;
  goBackURL: string;
  verifyEmailTitleText: string;
  verifyEmailConfirmationText: string;
  verifyEmailGoBackText: string;
  messageOnEmailConfirmationSuccess: string;
  messageOnError: string;
  sendNewVerificationEmailText: string;
  signOutText: string;
}

const defaultTranslations = {
  verifyEmailTitleText: 'Confirm your e-mail address!',
  verifyEmailConfirmationText:
    'A confirmation e-mail has been sent.' +
    ' Check your inbox and click on the link "Confirm my e-mail" to confirm your e-mail address.',
  verifyEmailGoBackText: 'Go back',
  sendNewVerificationEmailText: 'Send new confirmation e-mail',
  signOutText: 'Sign out',
  messageOnEmailConfirmationSuccess:
    'A new confirmation e-mail has been sent. Please check your inbox.',
};

@Component({
  selector: 'pt-auth-firebaseui-email-confirmation',
  templateUrl: './email-confirmation.component.html',
  styleUrls: ['./email-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [AuthProcessService],
})
export class EmailConfirmationComponent implements OnInit, OnChanges {
  @Input() email: string;
  @Input() goBackURL: string;
  // i18n translations to use in default template
  @Input() verifyEmailTitleText: string;
  @Input() verifyEmailConfirmationText: string;
  @Input() verifyEmailGoBackText: string;
  @Input() sendNewVerificationEmailText: string;
  @Input() signOutText: string;
  @Input() messageOnEmailConfirmationSuccess: string;

  // Template to use in place of default template
  @Input() template: TemplateRef<unknown>;

  @Output() signOut = new EventEmitter();

  // Final template
  verifyEmailTemplate: TemplateRef<unknown>;
  // Context hash to use for verifyEmailTemplate.
  verifyEmailContext: IVerifyEmailContext;

  isLoading: boolean;

  @ViewChild('defaultVerifyEmail', { static: true })
  defaultTemplate: TemplateRef<unknown>;

  constructor(
    public authProcess: AuthProcessService,
    private _router: Router,
    private _changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.template && isNil(changes.template.currentValue)) {
      this.verifyEmailTemplate = this.defaultTemplate;
    }
    this.verifyEmailContext = this._createTemplateContext();
  }

  ngOnInit(): void {
    if (!this.verifyEmailTemplate) {
      this.verifyEmailTemplate = this.defaultTemplate;
    }
    this.verifyEmailContext = this._createTemplateContext();
  }

  async continue(): Promise<void> {
    try {
      await this.authProcess.reloadUserInfo();
      await this._router.navigate([this.goBackURL]);
    } catch (error) {
      if (isError(error)) {
        this.authProcess.notifyError(error);
      }
    }
  }

  async sendNewVerificationEmail(): Promise<void> {
    try {
      this.isLoading = true;
      this._changeDetectorRef.markForCheck();
      await this.authProcess.sendNewVerificationEmail();
      this.authProcess.showToast(
        this.verifyEmailContext.messageOnEmailConfirmationSuccess
      );
    } catch (error) {
      if (isError(error)) {
        this.authProcess.notifyError(error);
      }
    } finally {
      this.isLoading = false;
      this._changeDetectorRef.markForCheck();
    }
  }

  private _createTemplateContext(): IVerifyEmailContext {
    return {
      email: this.email,
      goBackURL: this.goBackURL,
      verifyEmailTitleText:
        this.verifyEmailTitleText || defaultTranslations.verifyEmailTitleText,
      verifyEmailConfirmationText:
        this.verifyEmailConfirmationText ||
        defaultTranslations.verifyEmailConfirmationText,
      verifyEmailGoBackText:
        this.verifyEmailGoBackText || defaultTranslations.verifyEmailGoBackText,
      sendNewVerificationEmailText:
        this.sendNewVerificationEmailText ||
        defaultTranslations.sendNewVerificationEmailText,
      signOutText: this.signOutText || defaultTranslations.signOutText,
      messageOnEmailConfirmationSuccess:
        this.messageOnEmailConfirmationSuccess ||
        defaultTranslations.messageOnEmailConfirmationSuccess,
      messageOnError: '',
    };
  }
}
